import React, { useState, useEffect, useMemo } from "react"
import moment from "moment/moment"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Stack from "@mui/material/Stack"
import Button from "@mui/material/Button"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import { useSnackbar } from "notistack"
import { PAYMENT_TYPE } from "constants/global"
import { SUBSCRIPTION_PRODUCT } from "constants/global"
import {
  updateMemberVerification,
  updateMemberBetaTester,
  updateMemberDemoAccount,
} from "services/member"
import { StyledMemberInfo } from "./Member.style"
import { listBadges, getMemberBadges, updateMemberBadges } from "services/badge"

const MemberInfo = ({ member, memberId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [isVerified, setIsVerified] = useState(member?.isVerified || false)
  const [isBetaTester, setIsBetaTester] = useState(member?.isBetaTester || false)
  const [isDemoAccount, setIsDemoAccount] = useState(member?.isDemoAccount || false)
  const [allBadges, setAllBadges] = useState([])
  const [memberBadges, setMemberBadges] = useState([]) // [{ badgeId, isPinned, pinnedOrder, badge }]
  const [savingBadges, setSavingBadges] = useState(false)

  // Update isVerified state when member prop changes
  useEffect(() => {
    setIsVerified(member?.isVerified || false)
    setIsBetaTester(member?.isBetaTester || false)
    setIsDemoAccount(member?.isDemoAccount || false)
  }, [member?.isVerified, member?.isBetaTester, member?.isDemoAccount])

  // Load badges (catalog + member assignments)
  useEffect(() => {
    const load = async () => {
      if (!memberId) return
      try {
        const [b1, b2] = await Promise.all([
          listBadges(),
          getMemberBadges({ memberId }),
        ])
        // both endpoints return cms-style response; verifyResponse is used upstream, but here we’re defensive
        const list = b1?.data?.list || b1?.list || []
        const mb = b2?.data?.list || b2?.list || []
        setAllBadges(list)
        setMemberBadges(mb)
      } catch (e) {
        // keep page usable even if badge APIs fail
      }
    }
    load()
  }, [memberId])

  const memberBadgeIdSet = useMemo(() => new Set((memberBadges || []).map((x) => x.badgeId)), [memberBadges])

  const toggleAssigned = (badgeId) => {
    const has = memberBadgeIdSet.has(badgeId)
    if (has) {
      setMemberBadges((prev) => prev.filter((x) => x.badgeId !== badgeId))
    } else {
      const badge = allBadges.find((b) => b.id === badgeId) || null
      setMemberBadges((prev) => [...prev, { badgeId, isPinned: 0, pinnedOrder: 0, badge }])
    }
  }

  const togglePinned = (badgeId) => {
    setMemberBadges((prev) => {
      const next = prev.map((x) => {
        if (x.badgeId !== badgeId) return x
        const willPin = !(x.isPinned === 1)
        return { ...x, isPinned: willPin ? 1 : 0 }
      })
      // reassign pinnedOrder sequentially for pinned badges
      let order = 1
      return next
        .map((x) => (x.isPinned === 1 ? { ...x, pinnedOrder: order++ } : { ...x, pinnedOrder: 0 }))
        .sort((a, b) => (b.isPinned || 0) - (a.isPinned || 0) || (a.pinnedOrder || 0) - (b.pinnedOrder || 0))
    })
  }

  const saveBadges = async () => {
    if (!memberId) return
    setSavingBadges(true)
    try {
      const payload = (memberBadges || []).map((x) => ({
        badgeId: x.badgeId,
        isPinned: x.isPinned ? 1 : 0,
        pinnedOrder: x.pinnedOrder || 0,
      }))
      const resp = await updateMemberBadges({ memberId, badges: payload })
      if (resp?.rtnCode && resp.rtnCode !== 200) throw new Error(resp.message || "Failed")
      enqueueSnackbar("Badges updated", { variant: "success" })
    } catch (e) {
      enqueueSnackbar(`Failed to update badges: ${e.message || e}`, { variant: "error" })
    } finally {
      setSavingBadges(false)
    }
  }

  const registerMethods = []
  if (member?.subId?.includes("auth0")) registerMethods.push("Manual")
  if (member?.googleThirdId?.includes("google")) registerMethods.push("Google")
  if (member?.facebookThirdId?.includes("facebook")) registerMethods.push("Facebook")

  return (
    <StyledMemberInfo>
      <Card className="member-overview-card">
        <CardContent>
          <div className="member-grid">
            <div className="member-field">
              <div className="field-label">Email</div>
              <div className="field-value">{member.email || "N/A"}</div>
            </div>
            <div className="member-field">
              <div className="field-label">Username</div>
              <div className="field-value">{member.userName || "N/A"}</div>
            </div>
            <div className="member-field">
              <div className="field-label">Member Nickname</div>
              <div className="field-value">{member.nickName || "N/A"}</div>
            </div>
            <div className="member-field">
              <div className="field-label">First Name</div>
              <div className="field-value">{member.firstName || "N/A"}</div>
            </div>
            <div className="member-field">
              <div className="field-label">Last Name</div>
              <div className="field-value">{member.lastName || "N/A"}</div>
            </div>
            <div className="member-field">
              <div className="field-label">Last Login</div>
              <div className="field-value">
                {member.lastLoginAt ? moment(member.lastLoginAt).format("lll") : "N/A"}
              </div>
            </div>
            <div className="member-field">
              <div className="field-label">Joined Date</div>
              <div className="field-value">
                {member.registerAt ? moment(member.registerAt).format("lll") : "N/A"}
              </div>
            </div>
            <div className="member-field">
              <div className="field-label">Status</div>
              <div className="field-value">
                {member.isActive ? (
                  <Chip label="Activated" color="success" size="small" />
                ) : (
                  <Chip label="Deactivated" color="error" size="small" />
                )}
              </div>
            </div>
          </div>

          <div className="section-block">
            <div className="section-title">Register Type</div>
            <Stack direction="row" spacing={1}>
              {registerMethods.length === 0 && <Chip label="N/A" size="small" />}
              {registerMethods.map((method) => (
                <Chip key={`register-method-${method}`} label={method} size="small" />
              ))}
            </Stack>
          </div>

          {member.vipInfo && (
            <div className="section-block">
              <div className="section-title">Current Subscription</div>
              <p className="meta-text">
                Product:{" "}
                <strong>
                  {SUBSCRIPTION_PRODUCT[member.vipInfo.productCode]
                    ? SUBSCRIPTION_PRODUCT[member.vipInfo.productCode]
                    : member.vipInfo.productCode}
                </strong>
              </p>
              <p className="meta-text">
                Valid from <strong>{moment(member.vipInfo.fromAt).format("LL")}</strong> to{" "}
                <strong>{moment(member.vipInfo.endAt).format("LL")}</strong>
              </p>
              <p className="meta-text">
                Payment from <strong>{PAYMENT_TYPE[member.vipInfo.payment]}</strong>
              </p>
            </div>
          )}

          <div className="section-block">
            <div className="section-title">Membership Verification Status</div>
          <FormControlLabel
            control={
              <Checkbox
                checked={isVerified}
                onChange={async (e) => {
                  if (!memberId) {
                    enqueueSnackbar("Member ID is missing. Cannot update verification status.", {
                      variant: "error",
                    })
                    return
                  }
                  
                  const newVerifiedStatus = e.target.checked
                  setIsVerified(newVerifiedStatus)
                  
                  try {
                    await updateMemberVerification({
                      memberId: parseInt(memberId, 10),
                      isVerified: newVerifiedStatus,
                    })
                    
                    enqueueSnackbar(`User verification ${newVerifiedStatus ? 'enabled' : 'disabled'} successfully`, {
                      variant: "success",
                    })
                  } catch (error) {
                    // Revert the checkbox state if API call fails
                    setIsVerified(!newVerifiedStatus)
                    enqueueSnackbar(`Failed to update verification status: ${error.message || error}`, {
                      variant: "error",
                    })
                  }
                }}
              />
            }
            label="Verified User"
          />
            <Typography variant="body2" className="switch-note">
            Check this box if the user has been verified. This may affect their access privileges.
          </Typography>
          </div>

          <div className="section-block">
            <div className="section-title">Beta Tester</div>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!isBetaTester}
                onChange={async (e) => {
                  if (!memberId) {
                    enqueueSnackbar("Member ID is missing. Cannot update beta tester flag.", {
                      variant: "error",
                    })
                    return
                  }

                  const newVal = e.target.checked
                  setIsBetaTester(newVal)
                  try {
                    await updateMemberBetaTester({
                      memberId: parseInt(memberId, 10),
                      isBetaTester: newVal,
                    })
                    enqueueSnackbar(`Beta tester ${newVal ? "enabled" : "disabled"} successfully`, {
                      variant: "success",
                    })
                  } catch (error) {
                    setIsBetaTester(!newVal)
                    enqueueSnackbar(`Failed to update beta tester flag: ${error.message || error}`, {
                      variant: "error",
                    })
                  }
                }}
              />
            }
            label="Beta tester"
          />
            <Typography variant="body2" className="switch-note">
            Enable to grant early access to beta features on production.
          </Typography>
          </div>

          <div className="section-block">
            <div className="section-title">Demo Account</div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!isDemoAccount}
                  onChange={async (e) => {
                    if (!memberId) {
                      enqueueSnackbar("Member ID is missing. Cannot update demo-account flag.", {
                        variant: "error",
                      })
                      return
                    }

                    const newVal = e.target.checked
                    setIsDemoAccount(newVal)
                    try {
                      await updateMemberDemoAccount({
                        memberId: parseInt(memberId, 10),
                        isDemoAccount: newVal,
                      })
                      enqueueSnackbar(
                        `Demo account ${newVal ? "enabled" : "disabled"} successfully`,
                        { variant: "success" }
                      )
                    } catch (error) {
                      setIsDemoAccount(!newVal)
                      enqueueSnackbar(
                        `Failed to update demo-account flag: ${error.message || error}`,
                        { variant: "error" }
                      )
                    }
                  }}
                />
              }
              label="Demo account"
            />
            <Typography variant="body2" className="switch-note">
              Boost analytics surfaces (dashboard cards, time chart, streak/score) for this
              member only. Use for sales / investor demos. Real data is never modified.
            </Typography>
          </div>

          <div className="section-block">
            <div className="section-title">Badges (choose + pin up to 3)</div>
            <div className="badge-chip-stack">
            {(allBadges || []).map((b) => {
              const assigned = memberBadgeIdSet.has(b.id)
              const mb = (memberBadges || []).find((x) => x.badgeId === b.id)
              const pinned = mb?.isPinned === 1
              return (
                <Chip
                  key={b.id}
                  label={pinned ? `📌 ${b.name}` : b.name}
                  variant={assigned ? "filled" : "outlined"}
                  color={assigned ? "primary" : "default"}
                  onClick={() => toggleAssigned(b.id)}
                  onDelete={assigned ? () => togglePinned(b.id) : undefined}
                  deleteIcon={assigned ? <span style={{ fontSize: 12 }}>{pinned ? "Unpin" : "Pin"}</span> : undefined}
                />
              )
            })}
            </div>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                className="save-badges-btn"
                variant="contained"
                disabled={savingBadges}
                onClick={saveBadges}
              >
              Save Badges
            </Button>
          </Stack>
          </div>
        </CardContent>
      </Card>
    </StyledMemberInfo>
  )
}

export default MemberInfo
